import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ item, isSearching = false, searchQuery = '', highlightSearchMatch, isUpdating = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    data: {
      type: 'item',
      stage: item.stage,
      item: item
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isUpdating ? 0.7 : 1),
    position: 'relative',
    cursor: isUpdating ? 'not-allowed' : 'grab',
    pointerEvents: isUpdating ? 'none' : 'auto',
  };

  // Check if this item matches search query
  const isSearchMatch = isSearching && searchQuery && (
    (item.projectName && item.projectName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.companyName && item.companyName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.pic && item.pic.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.email && item.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.sector && item.sector.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.leadSource && item.leadSource.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (item.stage && item.stage.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className="card card-sm mb-3"
        style={{
          boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.3)' : 'none',
          transform: isDragging ? 'scale(1.02)' : 'scale(1)',
          transition: 'box-shadow 0.2s, transform 0.2s',
          border: isSearchMatch ? '2px solid rgba(var(--tblr-warning-rgb), 0.5)' : undefined,
          backgroundColor: isSearchMatch ? 'rgba(var(--tblr-warning-rgb), 0.05)' : undefined,
        }}
      >
        <div className="card-body">
          {isSearchMatch && (
            <div className="mb-2">
              <span className="badge bg-warning text-warning-fg" style={{ fontSize: '10px' }}>
                🔍 Search Match
              </span>
            </div>
          )}
          
          <h3 className="card-title">
            {highlightSearchMatch ? highlightSearchMatch(item.companyName) : item.companyName}
          </h3>
          
          <div className="text-secondary mb-3">
            {highlightSearchMatch ? highlightSearchMatch(item.description) : item.description}
          </div>
          
          <div className="badges-list mb-3">
            {item.certifications && item.certifications.map((cert, index) => (
              <span key={index} className={`badge bg-${['default', 'blue', 'azure', 'indigo'][index % 4]} text-${['default', 'blue', 'azure', 'indigo'][index % 4]}-fg`}>
                {cert}
              </span>
            ))}
          </div>
          
          <div className="mt-4">
            <div className="row">
              <div className="col">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar-list">
                    <span className="avatar avatar-xs rounded" style={{ backgroundImage: `url(${item.assignee?.avatar})` }}></span>
                  </div>
                  <div className="text-secondary">
                    {highlightSearchMatch ? highlightSearchMatch(item.assignee?.name) : item.assignee?.name}
                  </div>
                </div>
              </div>
              <div className="col-auto text-secondary">
                <div className="text-secondary">
                  {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'No due date'}
                </div>
              </div>
            </div>
          </div>
          
          {/* Show additional search-relevant info when searching */}
          {isSearching && (
            <div className="mt-3 pt-3 border-top">
              <div className="row g-2">
                <div className="col-6">
                  <small className="text-muted">Stage:</small>
                  <div className="text-secondary">
                    {highlightSearchMatch ? highlightSearchMatch(item.stage) : item.stage}
                  </div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Sector:</small>
                  <div className="text-secondary">
                    {highlightSearchMatch ? highlightSearchMatch(item.sector) : item.sector}
                  </div>
                </div>
                {item.email && (
                  <div className="col-12">
                    <small className="text-muted">Email:</small>
                    <div className="text-secondary">
                      {highlightSearchMatch ? highlightSearchMatch(item.email) : item.email}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SortableItem; 